// frontend/src/pages/TechnologyPage/TechnologyPage.jsx

import React, { useCallback, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useTranslation } from 'react-i18next';
import { Box, Typography, List, ListItem, ListItemText, Paper, Avatar } from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import { Helmet } from 'react-helmet-async';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import './TechnologyPage.css';

// Import the image
import ecosystemImage from './assets/scam.webp';

// Animation variants
const gridContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const gridItemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const TechPillar = ({ title, text, icon }) => (
    <motion.div variants={gridItemVariants}>
        <Paper elevation={2} className="tech-pillar-card">
            <Avatar className="tech-pillar-icon">{icon}</Avatar>
            <Typography variant="h6" component="h3">{title}</Typography>
            <Typography variant="body2">{text}</Typography>
        </Paper>
    </motion.div>
);

const TechnologyPage = () => {
    const { t } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: {
            events: { onHover: { enable: true, mode: 'repulse' }, resize: true },
            modes: { repulse: { distance: 100, duration: 0.4 } },
        },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 },
            move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false },
            number: { density: { enable: true, area: 800 }, value: 80 },
            opacity: { value: 0.5 },
            shape: { type: 'circle' },
            size: { value: { min: 1, max: 3 } },
        },
        detectRetina: true,
    };

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`tech-page-container ${isScrolled ? 'scrolled' : ''}`}
        >
            <Particles id="tsparticles-technology" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('technologyPage.helmet.title')}</title>
                <meta name="description" content={t('technologyPage.helmet.description')} />
            </Helmet>
            
            <div className="tech-nav-container">
                <UniversalNav />
            </div>

            <main className="tech-content-wrapper">
                <Box className="tech-glass-container">
                    
                    {/* 1. MAIN HEADER */}
                    <Box className="tech-header">
                        <Typography variant="h3" component="h1">
                            {t('technologyPage.mainTitle')}
                        </Typography>
                        <Typography variant="subtitle1" component="p">
                            {t('technologyPage.subtitle')}
                        </Typography>
                    </Box>

                    {/* 2. SPLIT SECTION: TEXT (Left) + IMAGE (Right) */}
                    <div className="tech-split-section">
                        <div className="tech-split-text">
                            <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 600, color: 'white' }}>
                                {t('technologyPage.techSplitTitle')}
                            </Typography>
                            <Typography paragraph component="p">
                                {t('technologyPage.techSplitParagraph')}
                            </Typography>
                        </div>
                        <div className="tech-split-image">
                            <img 
                                src={ecosystemImage} 
                                alt="Vetra AI Ecosystem" 
                                className="tech-ecosystem-image"
                            />
                        </div>
                    </div>

                    {/* 3. ARCHITECTURE INTRO (Full Width) */}
                    <Box className="tech-section" sx={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 3rem auto' }}>
                        <Typography paragraph component="p" sx={{ fontSize: '1.1rem' }}>
                            {t('technologyPage.introParagraph')}
                        </Typography>
                    </Box>
                    
                    {/* 4. PILLAR GRID */}
                    <motion.div
                        className="tech-pillar-grid"
                        variants={gridContainerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <TechPillar
                            title={t('technologyPage.pillars.aiAnalysis.title')}
                            text={t('technologyPage.pillars.aiAnalysis.text')}
                            icon={<MemoryIcon />}
                        />
                        <TechPillar
                            title={t('technologyPage.pillars.fingerprinting.title')}
                            text={t('technologyPage.pillars.fingerprinting.text')}
                            icon={<FingerprintIcon />}
                        />
                        <TechPillar
                            title={t('technologyPage.pillars.ledger.title')}
                            text={t('technologyPage.pillars.ledger.text')}
                            icon={<StorageIcon />} // ICON UPDATED HERE
                        />
                        <TechPillar
                            title={t('technologyPage.pillars.infrastructure.title')}
                            text={t('technologyPage.pillars.infrastructure.text')}
                            icon={<SecurityIcon />}
                        />
                    </motion.div>

                    {/* 5. FINGERPRINTING DETAILS */}
                    <Box className="tech-section" sx={{ mt: 6 }}>
                        <Typography variant="h5" component="h2">{t('technologyPage.fingerprintsSection.title')}</Typography>
                        <Typography  component="p">{t('technologyPage.fingerprintsSection.intro')}</Typography>
                        <List>
                            <ListItem>
                                <ListItemText 
                                    primary={t('technologyPage.fingerprintsSection.types.exact.primary')}
                                    secondary={t('technologyPage.fingerprintsSection.types.exact.secondary')} />
                            </ListItem>
                            <ListItem>
                                <ListItemText 
                                    primary={t('technologyPage.fingerprintsSection.types.perceptual.primary')} 
                                    secondary={t('technologyPage.fingerprintsSection.types.perceptual.secondary')} />
                            </ListItem>
                        </List>
                    </Box>

                    <Box className="tech-section">
                        <Typography variant="h5" component="h2">{t('technologyPage.commitmentSection.title')}</Typography>
                        <Typography  component="p">{t('technologyPage.commitmentSection.text')}</Typography>
                    </Box>
                </Box>
            </main>
        </div>
    );
};

export default TechnologyPage;